import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, resolveImg } from "@/lib/api";
import { UploadSimple, X, LinkSimple, CaretLeft, CaretRight } from "@phosphor-icons/react";

export function ImageField({ value, onChange, label, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Image too large (max 50MB).");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {label && <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{label}</label>}
      <div className="flex items-start gap-4">
        <div className="h-24 w-24 shrink-0 bg-muted border border-border overflow-hidden grid place-items-center">
          {value ? (
            <img src={resolveImg(value)} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] text-muted-foreground">No image</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={testid ? `${testid}-upload` : undefined}
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] border border-border px-3 py-2 hover:bg-secondary transition-colors disabled:opacity-50"
            >
              <UploadSimple size={15} /> {busy ? "Uploading…" : "Upload"}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-destructive px-2 py-2">
                <X size={14} /> Remove
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <LinkSimple size={14} className="text-muted-foreground" />
            <input
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="or paste image URL"
              className="flex-1 bg-transparent border-b border-border text-xs py-1 focus:outline-none focus:border-foreground"
            />
          </div>
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
}

export function MultiImageField({ value = [], onChange, label }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const upload = async (files) => {
    const tooBig = files.find((f) => f.size > 50 * 1024 * 1024);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is too large (max 50MB).`);
      return;
    }
    setBusy(true);
    try {
      const urls = [...value];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      onChange(urls);
      toast.success("Images uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  const [urlDraft, setUrlDraft] = useState("");
  const addUrl = () => {
    const u = urlDraft.trim();
    if (!u) return;
    onChange([...value, u]);
    setUrlDraft("");
    toast.success("Image URL added");
  };

  const move = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const arr = [...value];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    onChange(arr);
  };

  return (
    <div>
      {label && <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{label}</label>}
      {value.length > 1 && (
        <p className="text-[10px] text-muted-foreground mb-2">Drag to reorder, or use the arrows. Order sets how images appear in the project gallery.</p>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {value.map((img, i) => (
          <div
            key={i}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIndex !== null && dragIndex !== i) move(dragIndex, i); setDragIndex(null); }}
            onDragEnd={() => setDragIndex(null)}
            data-testid={`gallery-item-${i}`}
            className={`relative aspect-square bg-muted border overflow-hidden group cursor-move transition-all ${dragIndex === i ? "opacity-40 border-foreground" : "border-border"}`}
          >
            <img src={resolveImg(img)} alt="" className="h-full w-full object-cover pointer-events-none" />
            <span className="absolute top-1 left-1 h-5 min-w-5 px-1 grid place-items-center rounded bg-background/90 text-[10px] font-medium">{i + 1}</span>
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`gallery-remove-${i}`}
              className="absolute top-1 right-1 h-6 w-6 grid place-items-center bg-background/90 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={14} />
            </button>
            <div className="absolute bottom-0 inset-x-0 flex justify-between bg-background/85 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => move(i, i - 1)}
                disabled={i === 0}
                data-testid={`gallery-move-left-${i}`}
                aria-label="Move earlier"
                className="flex-1 grid place-items-center py-1 hover:bg-secondary disabled:opacity-30"
              >
                <CaretLeft size={14} />
              </button>
              <button
                type="button"
                onClick={() => move(i, i + 1)}
                disabled={i === value.length - 1}
                data-testid={`gallery-move-right-${i}`}
                aria-label="Move later"
                className="flex-1 grid place-items-center py-1 hover:bg-secondary disabled:opacity-30"
              >
                <CaretRight size={14} />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          data-testid="gallery-add"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="aspect-square border border-dashed border-border grid place-items-center text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-50"
        >
          <UploadSimple size={20} />
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files))} />
      <div className="mt-2 flex gap-2">
        <input
          data-testid="gallery-url-input"
          value={urlDraft}
          onChange={(e) => setUrlDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
          placeholder="or paste image URL and press Add"
          className="flex-1 h-9 px-3 text-xs bg-background border border-border focus:outline-none focus:border-foreground/40"
        />
        <button
          type="button"
          data-testid="gallery-url-add"
          onClick={addUrl}
          className="h-9 px-4 text-xs uppercase tracking-[0.15em] border border-border hover:bg-secondary transition-colors"
        >
          Add
        </button>
      </div>
    </div>
  );
}

export function VideoField({ value, onChange, label, testid = "hero-video-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [phase, setPhase] = useState("");

  const CHUNK = 4 * 1024 * 1024;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video too large (max 100MB). Please compress it first.");
      return;
    }
    setBusy(true);
    setPct(0);
    setPhase("uploading");
    try {
      const uploadId = crypto.randomUUID();
      const total = Math.ceil(file.size / CHUNK) || 1;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        try {
          await api.post("/admin/upload-video/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch {
          await sleep(1500);
          await api.post("/admin/upload-video/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        setPct(Math.round(((i + 1) / total) * 100));
      }
      const { data } = await api.post("/admin/upload-video/finish", {
        upload_id: uploadId,
        filename: file.name,
        total_chunks: total,
        total_size: file.size,
      });
      setPhase("processing");
      for (let tries = 0; tries < 120; tries++) {
        await sleep(3000);
        const { data: s } = await api.get(`/admin/upload-video/status/${data.media_id}`);
        if (s.failed) throw new Error("Video processing failed. Please try a different file.");
        if (s.ready) {
          onChange(data.url);
          toast.success("Video uploaded");
          return;
        }
      }
      throw new Error("Video processing timed out. Please try a shorter video.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setBusy(false);
      setPct(0);
      setPhase("");
    }
  };

  const isFile = value && (value.includes("/media-file/") || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(value));

  return (
    <div>
      {label && <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{label}</label>}
      <div className="flex items-start gap-4">
        <div className="h-24 w-40 shrink-0 bg-black border border-border overflow-hidden grid place-items-center">
          {value ? (
            isFile ? (
              <video src={resolveImg(value)} muted playsInline className="h-full w-full object-cover" />
            ) : (
              <span className="text-[10px] text-muted-foreground px-2 text-center">External video link</span>
            )
          ) : (
            <span className="text-[10px] text-muted-foreground">No video</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={testid}
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] border border-border px-3 py-2 hover:bg-secondary transition-colors disabled:opacity-50"
            >
              <UploadSimple size={15} /> {busy ? (phase === "processing" ? "Processing…" : `Uploading… ${pct}%`) : "Upload video"}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-destructive px-2 py-2">
                <X size={14} /> Remove
              </button>
            )}
          </div>
          {busy && (
            <div className="h-1 w-full bg-border overflow-hidden rounded-full">
              <div
                className={`h-full bg-accent transition-all duration-200 ${phase === "processing" ? "animate-pulse" : ""}`}
                style={{ width: phase === "processing" ? "100%" : `${pct}%` }}
              />
            </div>
          )}
          {phase === "processing" && (
            <p className="text-[10px] text-muted-foreground">Optimising video for the web — this can take a minute or two. Keep this tab open.</p>
          )}
          <div className="flex items-center gap-2">
            <LinkSimple size={14} className="text-muted-foreground" />
            <input
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="or paste mp4 / YouTube / Vimeo URL"
              className="flex-1 bg-transparent border-b border-border text-xs py-1 focus:outline-none focus:border-foreground"
            />
          </div>
          <p className="text-[10px] text-muted-foreground">Best: a short, muted, looping mp4 under ~100MB. YouTube / Vimeo links also work.</p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/x-m4v,video/*"
        className="hidden"
        onChange={(e) => upload(e.target.files[0])}
      />
    </div>
  );
}

